import {
  RbacPermissions, RbacRole, RbacRoleAction,
} from "../types/Rbac";
import {RbacRoleActionType} from "../types/RbacActionTypes";
import SecurityContextStorage from "../services/security-context-storage";
import {SecurityContext} from "../types/SecurityContext";

export const FULL_PERMISSIONS: RbacPermissions = {
  canRead: true,
  canWrite: true,
  canDelete: true,
  canBind: true
};

export const NO_PERMISSIONS: RbacPermissions = {
  canRead: false,
  canWrite: false,
  canDelete: false,
  canBind: false
};

const WILDCARD = '*';

export function mapRoles(data: any): Array<RbacRole> {
  return data.roles.map((role: any) => {
    return {
      name: role.name,
      description: role.description,
      actions: mapRoleActions(role.actions),
      notActions: mapRoleActions(role.notActions)
    };
  });
}

function mapRoleActions(actions: Array<any>): Array<RbacRoleAction> {
  if (actions && actions.length > 0) {
    return actions.map((action: any) => {
      return {
        schema: action.object,
        actionType: action.actionType
      };
    });
  }
  return [];
}

export function getPermissions(schemaId: string): RbacPermissions {
  if (SecurityContextStorage.hasSecurityContext()) {
    const context = SecurityContextStorage.getContext();
    if (context.enabled && context.roles) {
      return {
        canRead: canRead(schemaId, context.roles),
        canWrite: canWrite(schemaId, context.roles),
        canDelete: canDelete(schemaId, context.roles),
        canBind: canBind(schemaId, context.roles)
      }
    }
  }
  return FULL_PERMISSIONS;
}

export function getUserPermissions(context: SecurityContext) {
  if (context.enabled && context.roles) {
    const readAllowed = context.roles.some((role: RbacRole) => {
      return hasAction(role.actions, RbacRoleActionType.READ);
    });
    const readForbidden = context.roles.some((role: RbacRole) => {
      return hasActionForAllSchemas(role.notActions, RbacRoleActionType.READ);
    });
    const createAllowed = context.roles.some((role: RbacRole) => {
      return hasAction(role.actions, RbacRoleActionType.WRITE);
    });
    const createForbidden = context.roles.some((role: RbacRole) => {
      return hasActionForAllSchemas(role.notActions, RbacRoleActionType.WRITE);
    });
    return {
      canRead: readForbidden ? false : readAllowed,
      canCreate: createForbidden ? false : createAllowed
    };
  }
  return {
    canRead: true,
    canCreate: true
  };
}

function canRead(schemaId: string, roles: Array<RbacRole>) {
  return traverse(schemaId, roles, RbacRoleActionType.READ);
}

function canWrite(schemaId: string, roles: Array<RbacRole>) {
  return traverse(schemaId, roles, RbacRoleActionType.WRITE);
}

function canDelete(schemaId: string, roles: Array<RbacRole>) {
  return traverse(schemaId, roles, RbacRoleActionType.DELETE);
}

function canBind(schemaId: string, roles: Array<RbacRole>) {
  return traverse(schemaId, roles, RbacRoleActionType.BIND);
}

function traverse(schemaId: string, roles: Array<RbacRole>, actionType: RbacRoleActionType): boolean {
  const actions: RbacRoleAction[] = [];
  const notActions: RbacRoleAction[] = [];

  // Collect all related actions and not actions
  roles.forEach(role => {
    actions.push(...filterActions(schemaId, role.actions));
    notActions.push(...filterActions(schemaId, role.notActions));
  });

  // First, check if there is an explicit not action, they are with precedence over actions
  if (notActions.length > 0 && hasAction(notActions, actionType)) {
    return false;
  }

  // Check if the action is allowed
  if (actions.length > 0 && hasAction(actions, actionType)) {
    return true;
  }

  return false;
}

function filterActions(schemaId: string, actions: RbacRoleAction[]) {
  return actions.filter(action => filterBySchemaId(schemaId, action));
}

function filterBySchemaId(schemaId: string, action: RbacRoleAction): boolean {
  if (action.schema === WILDCARD) {
    return true;
  } else if (action.schema === schemaId) {
    return true;
  } else if (action.schema.indexOf(WILDCARD) > -1) {
    return toRegex(action.schema).test(schemaId);
  }
  return false;
}

function toRegex(schema: string) {
  const schemaExp = schema.replace(WILDCARD, '.*');
  return new RegExp(`^${schemaExp}$`);
}

function hasAction(actions: RbacRoleAction[], actionType: RbacRoleActionType) {
  return actions.some(action => {
    return action.actionType === actionType || action.actionType === RbacRoleActionType.ALL;
  });
}

function hasActionForAllSchemas(actions: RbacRoleAction[], actionType: RbacRoleActionType) {
  return actions.some(action => {
    return action.schema === WILDCARD && (action.actionType === actionType || action.actionType === RbacRoleActionType.ALL);
  });
}
